import React from 'react';
import { motion } from 'framer-motion';
import { GlobeIcon, MessageCircleIcon, MonitorIcon, SparklesIcon, BotIcon, ClockIcon, TrendingUpIcon, ShieldCheckIcon, CheckIcon } from 'lucide-react';
const CHANNELS = [{
  id: 'Website Widget',
  icon: <GlobeIcon className="w-5 h-5" data-id="element-53" />,
  desc: 'A chat assistant embedded on your website that answers product questions and captures leads.',
  accent: 'bg-primary-indigo/10 text-primary-indigo'
}, {
  id: 'WhatsApp',
  icon: <MessageCircleIcon className="w-5 h-5" data-id="element-54" />,
  desc: 'Let buyers message your business on WhatsApp and get instant, on-brand replies.',
  accent: 'bg-green-100 text-green-600'
}];
const VALUE = [{
  icon: <ClockIcon className="w-4 h-4" data-id="element-55" />,
  title: 'Replies 24/7',
  desc: 'Never miss a buyer — your assistant answers instantly, day or night.'
}, {
  icon: <TrendingUpIcon className="w-4 h-4" data-id="element-56" />,
  title: 'Qualifies & captures leads',
  desc: 'Understands intent, answers questions and pushes hot leads to your inbox.'
}, {
  icon: <ShieldCheckIcon className="w-4 h-4" data-id="element-57" />,
  title: 'Always on-brand',
  desc: 'Trained on your catalogue and rules so every answer is accurate.'
}];
export function BusinessAIChannels() {
  return <div className="w-full flex flex-col pb-24" data-id="element-58">
      {/* Hero */}
      <motion.div initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} className="flex flex-col items-center text-center pt-2 pb-6" data-id="element-59">
        <div className="relative mb-5" data-id="element-60">
          <div className="w-[72px] h-[72px] rounded-3xl bg-gradient-to-br from-primary-indigo to-ai-purple flex items-center justify-center shadow-xl" data-id="element-61">
            <BotIcon className="w-9 h-9 text-white" data-id="element-62" />
          </div>
          <motion.div animate={{
          scale: [1, 1.15, 1],
          opacity: [0.6, 0.2, 0.6]
        }} transition={{
          duration: 2.5,
          repeat: Infinity
        }} className="absolute inset-0 rounded-3xl bg-primary-indigo/30 blur-xl -z-10" data-id="element-63" />
          <div className="absolute -right-2 -top-2 w-7 h-7 rounded-full bg-white shadow-md flex items-center justify-center" data-id="element-64">
            <SparklesIcon className="w-3.5 h-3.5 text-ai-purple" data-id="element-65" />
          </div>
        </div>
        <h2 className="text-xl font-display font-bold text-dark-navy mb-1.5" data-id="element-66">
          AI Channels
        </h2>
        <p className="text-sm text-slate-500 leading-relaxed max-w-[300px]" data-id="element-67">
          A smart AI assistant that talks to your buyers across your website and
          WhatsApp — answering questions and capturing leads automatically.
        </p>
      </motion.div>

      {/* Channels */}
      <div className="flex flex-col gap-3 mb-6" data-id="element-68">
        {CHANNELS.map(ch => <div key={ch.id} className="bg-white border border-slate-200/70 rounded-2xl p-4 shadow-[0_1px_3px_rgb(0,0,0,0.03)] flex items-start gap-3" data-id="element-69">
            <span className={`w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0 ${ch.accent}`} data-id="element-70">
              {ch.icon}
            </span>
            <div className="min-w-0" data-id="element-71">
              <div className="font-bold text-dark-navy text-[15px] leading-tight mb-0.5" data-id="element-72">
                {ch.id}
              </div>
              <p className="text-[13px] text-slate-500 leading-snug" data-id="element-73">
                {ch.desc}
              </p>
            </div>
          </div>)}
      </div>

      {/* Value */}
      <div className="mb-6" data-id="element-74">
        <h3 className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 px-1 mb-2.5" data-id="element-75">
          What it does for you
        </h3>
        <div className="bg-white border border-slate-200/70 rounded-2xl divide-y divide-slate-100 shadow-[0_1px_3px_rgb(0,0,0,0.03)]" data-id="element-76">
          {VALUE.map(v => <div key={v.title} className="flex items-start gap-3 p-4" data-id="element-77">
              <span className="w-8 h-8 rounded-xl bg-primary-indigo/10 text-primary-indigo flex items-center justify-center flex-shrink-0" data-id="element-78">
                {v.icon}
              </span>
              <div data-id="element-79">
                <div className="font-semibold text-dark-navy text-sm leading-tight mb-0.5" data-id="element-80">
                  {v.title}
                </div>
                <p className="text-[13px] text-slate-500 leading-snug" data-id="element-81">
                  {v.desc}
                </p>
              </div>
            </div>)}
        </div>
      </div>

      {/* Web portal callout */}
      <div className="rounded-3xl bg-dark-navy text-white p-5 shadow-xl" data-id="element-82">
        <div className="flex items-center gap-2.5 mb-3" data-id="element-83">
          <span className="w-10 h-10 rounded-2xl bg-white/10 flex items-center justify-center flex-shrink-0" data-id="element-84">
            <MonitorIcon className="w-5 h-5 text-electric-cyan" data-id="element-85" />
          </span>
          <div data-id="element-86">
            <div className="font-bold text-[15px] leading-tight" data-id="element-87">
              Set up on the web portal
            </div>
            <div className="text-[12px] text-white/60" data-id="element-88">
              talab.app · desktop only
            </div>
          </div>
        </div>
        <p className="text-[13px] text-white/70 leading-relaxed mb-4" data-id="element-89">
          Configuring your website widget and WhatsApp assistant — branding,
          embed code, phone number connection and AI behaviour — is done on the
          Talab web portal for the best experience.
        </p>
        <div className="flex flex-col gap-2 mb-4" data-id="element-90">
          {['Customize the widget look & welcome message', 'Copy your embed code or connect WhatsApp', 'Tune AI answers and hand-off rules'].map(step => <div key={step} className="flex items-center gap-2.5" data-id="element-91">
              <span className="w-5 h-5 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0" data-id="element-92">
                <CheckIcon className="w-3 h-3 text-electric-cyan" data-id="element-93" />
              </span>
              <span className="text-[13px] text-white/80" data-id="element-94">{step}</span>
            </div>)}
        </div>
        <div className="w-full py-3 bg-white/10 text-white text-sm font-bold rounded-2xl flex items-center justify-center gap-2 border border-white/10" data-id="element-95">
          <MonitorIcon className="w-4 h-4" data-id="element-96" />
          Open talab.app on desktop to configure
        </div>
        <p className="text-[11px] text-white/40 text-center mt-3 leading-snug" data-id="element-97">
          Sign in to talab.app on your computer and open Business Hub → AI
          Channels.
        </p>
      </div>
    </div>;
}